import React, { useState, useEffect, useRef } from 'react';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';

function Home() {
  const [activeTab, setActiveTab] = useState('pc');
  const [isLoaded, setIsLoaded] = useState(false);
  const pricingGridRef = useRef(null);
  const featuresGridRef = useRef(null);
  const { formatFromEur } = useCurrency();

  const scrollPricing = (direction) => {
    const el = pricingGridRef.current;
    if (!el) return;
    const card = el.querySelector('.pricing-card');
    const gap = 14;
    const step = card ? card.getBoundingClientRect().width + gap : 280;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const scrollFeatures = (direction) => {
    const el = featuresGridRef.current;
    if (!el) return;
    const card = el.querySelector('.feature-card');
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || '12') || 12;
    const step = card ? card.getBoundingClientRect().width + gap : 280;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  useEffect(() => {
    setIsLoaded(true);
    setPageMeta({
      title: 'Elorimi — Cloud PC & Cloud VPS',
      description: 'Elorimi provides on-demand Cloud PC and Cloud VPS resources with transparent pricing and instant deployment.',
    });
  }, []);

  return (
    <div className={isLoaded ? 'loaded' : ''}>
      {/* Optimized Animated Background */}
      <div className="bg-animation">
        <div className="bg-mesh"></div>
        <div className="bg-grid"></div>
        <div className="bg-glow bg-glow-1"></div>
        <div className="bg-glow bg-glow-2"></div>
        <div className="bg-glow bg-glow-3"></div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="hero-title">
                High-Performance 
                <br />
                <span className="gradient-text">Cloud Computing</span>
              </h1>
              
              <p className="hero-subtitle">
                Launch powerful Cloud PCs and VPS servers in minutes. Built for development, design, AI workloads, automation, and high-demand tasks - with reliable performance, secure access, and ultra-low latency from anywhere in the world.
              </p>
              
              <div className="hero-actions">
                <a href="/dashboard" className="btn btn-primary btn-lg">
                  Get Started
                </a>
                <a href="#features" className="btn btn-outline-primary btn-lg">
                  Learn More
                </a>
              </div>
              
              <div className="hero-stats">
                <div className="stats-item">
                  <div>
                    <div>
                      <div className="stats-value">Simple</div>
                      <div className="stats-label">Setup Process</div>
                    </div>
                  </div>
                </div>
                <div className="stats-divider"></div>
                <div className="stats-item">
                  <div>
                    <div className="stats-value">24/7</div>
                    <div className="stats-label">Support</div>
                  </div>
                </div>
                <div className="stats-divider"></div>
                <div className="stats-item">
                  <div>
                    <div className="stats-value">Multi-Use</div>
                    <div className="stats-label">AI, 3D, Development</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="hero-visual">
                <div className="gpu-container">
                  <div className="gpu-core">
                    <i className="fas fa-microchip"></i>
                  </div>
                  <div className="gpu-ring ring-1"></div>
                  <div className="gpu-ring ring-2"></div>
                </div>
                <div className="hero-floating-cards">
                  <div className="floating-card glass-hover">
                    <div className="card-icon">
                      <i className="fas fa-tachometer-alt"></i>
                    </div>
                    <div className="card-value">99.99%</div>
                    <div className="card-label">Uptime SLA</div>
                  </div>
                  <div className="floating-card glass-hover" style={{ animationDelay: '1s' }}>
                    <div className="card-icon">
                      <i className="fas fa-bolt"></i>
                    </div>
                    <div className="card-value">40 Gbps</div>
                    <div className="card-label">Network Speed</div>
                  </div>
                  <div className="floating-card glass-hover" style={{ animationDelay: '2s' }}>
                    <div className="card-icon">
                      <i className="fas fa-gem"></i>
                    </div>
                    <div className="card-value">8x</div>
                    <div className="card-label">Performance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Getting Started</span>
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Get started with your Cloud PC or VPS in just a few simple steps — no complex setup, no technical expertise required
            </p>
          </div>
          
          <div className="steps-container">
            <div className="step-card glass-hover">
              <div className="step-number">01</div>
              <div className="step-icon gradient-bg">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="step-title">Choose Configuration</h3>
              <p className="step-description">
                Select the performance level that matches your workload - from lightweight servers to high-performance cloud machines designed for demanding tasks.
              </p>
            </div>
            
            <div className="step-connector">
              <i className="fas fa-chevron-right icon-right"></i>
              <i className="fas fa-chevron-down icon-down"></i>
            </div>
            
            <div className="step-card glass-hover">
              <div className="step-number">02</div>
              <div className="step-icon gradient-bg-2">
                <i className="fas fa-download"></i>
              </div>
              <h3 className="step-title">Receive Instant Access</h3>
              <p className="step-description">
                Once your booking is confirmed, we prepare your dedicated environment and send your secure connection details shortly before your session begins.
              </p>
            </div>
            
            <div className="step-connector">
              <i className="fas fa-chevron-right icon-right"></i>
              <i className="fas fa-chevron-down icon-down"></i>
            </div>
            
            <div className="step-card glass-hover">
              <div className="step-number">03</div>
              <div className="step-icon gradient-bg-3">
                <i className="fas fa-play"></i>
              </div>
              <h3 className="step-title">Connect and Get to Work</h3>
              <p className="step-description">
                Log in from anywhere using a fast and secure remote connection. Your cloud machine is ready to handle development, design, AI workloads, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Features</span>
            <h2 className="section-title">Powerful Features</h2>
            <p className="section-subtitle">
             Everything you need for reliable, secure, and high-performance cloud computing
            </p>
          </div>
          
          <div className="features-slider">
            <button className="pricing-arrow prev" onClick={() => scrollFeatures(-1)} aria-label="Previous">
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button className="pricing-arrow next" onClick={() => scrollFeatures(1)} aria-label="Next">
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
            <div className="features-grid" ref={featuresGridRef}>
            <div className="feature-card glass-hover">
              <div className="feature-icon">
                <i className="fas fa-globe"></i>
              </div>
              <h3 className="feature-title">Global Infrastructure</h3>
              <p className="feature-description">
                Work without borders. Connect from multiple data centers worldwide with low latency.
              </p>
            </div>
            
            <div className="feature-card glass-hover">
              <div className="feature-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="feature-title">Isolated Environments</h3>
              <p className="feature-description">
                Each Cloud PC runs in a fully isolated VM with encrypted connections.
              </p>
            </div>
            
            <div className="feature-card glass-hover">
              <div className="feature-icon">
                <i className="fas fa-lock"></i>
              </div>
              <h3 className="feature-title">Secure Payments</h3>
              <p className="feature-description">
                All payments are processed through secure, encrypted systems.
              </p>
            
            </div>
            
            <div className="feature-card glass-hover">
              <div className="feature-icon">
                <i className="fas fa-desktop"></i>
              </div>
              <h3 className="feature-title">Use Any Device</h3>
              <p className="feature-description">
                Laptop, tablet, or old computer — all you need is an internet connection.
              </p>
            </div>
            
            <div className="feature-card glass-hover">
              <div className="feature-icon">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <h3 className="feature-title">Flexible Billing</h3>
              <p className="feature-description">
                Pay only for what you use. No hidden costs, no long-term commitments.
              </p>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Pricing</span>
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">
              Pay only for what you use. No subscriptions, no hidden fees.
            </p>
          </div>
          
          <div className="pricing-tabs glass">
            <button
              className={`pricing-tab ${activeTab === 'pc' ? 'active' : ''}`}
              onClick={() => setActiveTab('pc')}
            >
              <i className="fas fa-desktop"></i>
              Cloud PC
            </button>
            <button
              className={`pricing-tab ${activeTab === 'vps' ? 'active' : ''}`}
              onClick={() => setActiveTab('vps')}
            >
              <i className="fas fa-server"></i>
              Cloud VPS
            </button>
          </div>

          {activeTab === 'pc' && (
            <div className="pricing-slider">
              <button className="pricing-arrow prev" onClick={() => scrollPricing(-1)} aria-label="Previous">
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button className="pricing-arrow next" onClick={() => scrollPricing(1)} aria-label="Next">
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
              <div className="pricing-grid" ref={pricingGridRef}>
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">Nvidia Tesla T4</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(1.18)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> 16GB RAM</li>
                  <li><i className="fas fa-check"></i> 4 vCPU Cores</li>
                  <li><i className="fas fa-check"></i> 100GB Storage</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
              
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">Nvidia L4</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(3.69)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> 32GB RAM</li>
                  <li><i className="fas fa-check"></i> 8 vCPU Cores</li>
                  <li><i className="fas fa-check"></i> 100GB Storage</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
              
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">Nvidia A10G</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(4.14)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> 32GB RAM</li>
                  <li><i className="fas fa-check"></i> 8 vCPU Cores</li>
                  <li><i className="fas fa-check"></i> 100GB Storage</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
              
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">Nvidia L40S</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(8.64)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> 32GB RAM</li>
                  <li><i className="fas fa-check"></i> 8 vCPU Cores</li>
                  <li><i className="fas fa-check"></i> 100GB Storage</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
              </div>
            </div>
          )}

          {activeTab === 'vps' && (
            <div className="pricing-slider">
              <button className="pricing-arrow prev" onClick={() => scrollPricing(-1)} aria-label="Previous">
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button className="pricing-arrow next" onClick={() => scrollPricing(1)} aria-label="Next">
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
              <div className="pricing-grid" ref={pricingGridRef}>
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">1 vCPU</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(0.02)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-microchip"></i> 2 vCPU Cores</li>
                  <li><i className="fas fa-memory"></i> 2GB RAM</li>
                  <li><i className="fas fa-hdd"></i> 50GB SSD Storage</li>
                  <li><i className="fas fa-network-wired"></i> 1Mbps Network</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Deploy Now</a>
              </div>
              
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">4 vCPU</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(0.08)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-microchip"></i> 4 vCPU Cores</li>
                  <li><i className="fas fa-memory"></i> 8GB RAM</li>
                  <li><i className="fas fa-hdd"></i> 50GB SSD Storage</li>
                  <li><i className="fas fa-network-wired"></i> 1Mbps Network</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Deploy Now</a>
              </div>
              
              <div className="pricing-card glass">
                <div className="pricing-header">
                  <h3 className="pricing-name">16 vCPU</h3>
                </div>
                <div className="pricing-price">
                  <div className="price-from">from</div>
                  <div className="price-amount">{formatFromEur(0.29)}</div>
                  <div className="price-period">/hour</div>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-microchip"></i> 16 vCPU Cores</li>
                  <li><i className="fas fa-memory"></i> 32GB RAM</li>
                  <li><i className="fas fa-hdd"></i> 50GB SSD Storage</li>
                  <li><i className="fas fa-network-wired"></i> 1Mbps Network</li>
                </ul>
                <a href="/dashboard" className="btn btn-outline-primary w-100">Deploy Now</a>
              </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card glass">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Get Started?</h2>
              <p className="cta-description">
                Join professionals, developers, and businesses who rely on our cloud infrastructure to power their projects with speed, security, and flexibility.
              </p>
              <div className="cta-actions">
                <a href="/dashboard" className="btn btn-primary btn-lg">
                  Get Started
                </a>
                <a href="/docs" className="btn btn-outline btn-lg">
                  Read Documentation
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
